package org.moxultimate.topwithmox.wallpapercrud.api

import io.ktor.client.*
import io.ktor.client.request.*
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.serialization.json.Json
import org.khronos.webgl.Int8Array
import org.moxultimate.topwithmox.core.base.BaseAPI.baseAPI
import org.moxultimate.topwithmox.core.handler.APIHandler
import org.moxultimate.topwithmox.core.state.APIState
import org.moxultimate.topwithmox.wallpaper.data.WallpaperRequest
import org.moxultimate.topwithmox.wallpaper.data.WallpaperResponse
import org.w3c.files.Blob
import org.w3c.xhr.FormData

/**
 * WallpaperCRUDRepository
 * Handles all write operations for wallpapers: upload, update, delete, and saving series/tags.
 * Read operations live in WallpaperRepository to keep concerns separate.
 *
 * @param httpClient Ktor HTTP client used for DELETE and POST requests.
 */
class WallpaperCRUDRepository(
    private val httpClient: HttpClient,
) {
    private val baseUrl = "$baseAPI/wallpapers"
    private val wallpaperSeriesUrl = "$baseAPI/wallpaper-series"
    private val wallpaperTagUrl = "$baseAPI/wallpaper-tag"

    /**
     * deleteWallpaperById
     * Deletes a wallpaper by [id] via DELETE /wallpapers/{id}.
     *
     * @param id ID of the wallpaper to delete.
     */
    fun deleteWallpaperById(id: Long): Flow<APIState<Unit>> = flow {
        emit(APIState.Loading)
        emit(APIHandler.safeApiCall {
            httpClient.delete("$baseUrl/$id") {}
        })
    }

    /**
     * uploadSingleWallpaper
     * Uploads a single wallpaper using the browser's native FormData + fetch().
     *
     * Ktor's Kotlin/JS multipart builder generates Content-Disposition headers that Spring 6's
     * strict ContentDisposition.parse() rejects. Using browser FormData bypasses this entirely —
     * the browser constructs the multipart body the same way Postman does.
     *
     * @param request Wallpaper data including image and thumbnail as ByteArrays.
     */
    suspend fun uploadSingleWallpaper(request: WallpaperRequest): APIState<WallpaperResponse> {
        return try {
            val form = FormData()

            val imageBlob = Blob(arrayOf(request.imagePath.unsafeCast<Int8Array>().buffer), imageType)
            val thumbBlob = Blob(arrayOf(request.thumbnailPath.unsafeCast<Int8Array>().buffer), imageType)
            form.append("image", imageBlob, "image.png")
            form.append("thumbnail", thumbBlob, "thumbnail.png")
            if (request.panoramaPath.isNotEmpty()) {
                val panoramaBlob = Blob(arrayOf(request.panoramaPath.unsafeCast<Int8Array>().buffer), imageType)
                form.append("panorama", panoramaBlob, "panorama.webp")
            }
            form.append("title", request.title)
            form.append("description", request.description)
            form.append("type", request.type)
            form.append("series", request.series)
            form.append("tags", request.tags)
            form.append("resolution", request.resolution)
            form.append("video_link", request.videoLink)

            val init: dynamic = js("{}")
            init.method = "POST"
            init.body = form

            val response = window.fetch(baseUrl, init).await()

            if (!response.ok) {
                val code = response.status.toInt()
                val text = response.text().await()
                return APIState.Error(APIHandler.handleErrorMessage(code, text), code)
            }

            val text = response.text().await()
            APIState.Success(lenientJson.decodeFromString<WallpaperResponse>(text))
        } catch (e: Throwable) {
            APIState.Error(e.message ?: "Upload failed")
        }
    }

    /**
     * updateWallpaper
     * Updates an existing wallpaper via PUT /wallpapers/{id}.
     * Only non-null fields are appended so the server treats omitted fields as unchanged.
     *
     * @param id ID of the wallpaper to update.
     */
    fun updateWallpaper(
        id: Long,
        title: String? = null,
        description: String? = null,
        type: String? = null,
        series: String? = null,
        tags: String? = null,
        resolution: String? = null,
        videoLink: String? = null,
        imageBytes: ByteArray? = null,
        thumbnailBytes: ByteArray? = null,
        panoramaBytes: ByteArray? = null,
    ): Flow<APIState<WallpaperResponse>> = flow {
        emit(APIState.Loading)
        emit(
            performUpdate(id, title, description, type, series, tags, resolution, videoLink, imageBytes, thumbnailBytes, panoramaBytes)
        )
    }

    private suspend fun performUpdate(
        id: Long,
        title: String?,
        description: String?,
        type: String?,
        series: String?,
        tags: String?,
        resolution: String?,
        videoLink: String?,
        imageBytes: ByteArray?,
        thumbnailBytes: ByteArray?,
        panoramaBytes: ByteArray?,
    ): APIState<WallpaperResponse> {
        return try {
            val form = FormData()
            imageBytes?.let {
                form.append("image", Blob(arrayOf(it.unsafeCast<Int8Array>().buffer), imageType), "image.png")
            }
            thumbnailBytes?.let {
                form.append("thumbnail", Blob(arrayOf(it.unsafeCast<Int8Array>().buffer), imageType), "thumbnail.png")
            }
            panoramaBytes?.let {
                form.append("panorama", Blob(arrayOf(it.unsafeCast<Int8Array>().buffer), imageType), "panorama.webp")
            }
            title?.let { form.append("title", it) }
            description?.let { form.append("description", it) }
            type?.let { form.append("type", it) }
            series?.let { form.append("series", it) }
            tags?.let { form.append("tags", it) }
            resolution?.let { form.append("resolution", it) }
            videoLink?.let { form.append("video_link", it) }

            val init: dynamic = js("{}")
            init.method = "PUT"
            init.body = form

            val response = window.fetch("$baseUrl/$id", init).await()

            if (!response.ok) {
                val code = response.status.toInt()
                val text = response.text().await()
                return APIState.Error(APIHandler.handleErrorMessage(code, text), code)
            }

            APIState.Success(lenientJson.decodeFromString<WallpaperResponse>(response.text().await()))
        } catch (e: Throwable) {
            APIState.Error(e.message ?: "Update failed")
        }
    }

    /**
     * saveSeries
     * Registers [series] via POST /wallpaper-series if it doesn't already exist.
     * Best-effort — failures are swallowed so they never block the upload flow.
     *
     * @param series Series name to register.
     */
    suspend fun saveSeries(series: String) {
        try {
            httpClient.post(wallpaperSeriesUrl) {
                parameter("series", series)
            }
        } catch (_: Throwable) {}
    }

    /**
     * saveTag
     * Registers a single [tag] via POST /wallpaper-tag if it doesn't already exist.
     * Best-effort — failures are swallowed so they never block the upload flow.
     *
     * @param tag Tag string to register.
     */
    suspend fun saveTag(tag: String) {
        try {
            httpClient.post(wallpaperTagUrl) {
                parameter("tag", tag)
            }
        } catch (_: Throwable) {}
    }
}

/** JSON instance that tolerates extra fields Spring adds (id, createdAt, etc.). */
private val lenientJson = Json { ignoreUnknownKeys = true }

/** Blob options for PNG images — created once via JS literal to avoid BlobPropertyBag init issues in Kotlin/JS. */
private val imageType: dynamic = js("({ type: 'image/png' })")